import type {
  ConsumerContractSubscription,
  DomainOwner,
  SourceCategory,
  SubscriptionKind,
  SubscriptionOperationProfile,
  SubscriptionRelationshipProfile,
  UsageKind,
} from './consumerContractRegistry.types';
import type { SummaryFieldDefinition, SummaryRowConsumerArtifact } from './consumerContractRegistry.summaryFields';
import {
  DESIGNER_SUMMARY_FIELD_DEFINITIONS,
  GET_DESIGNER_SUMMARY_ROWS_REF,
  SUMMARY_ROW_CONSUMER_ARTIFACTS,
} from './consumerContractRegistry.summaryFields';
import { operationPreset, type OperationPresetKey } from './consumerContractRegistry.operationEffects';
import {
  PUBLIC_PDF_MAPPER,
  relationshipPreset,
  SHARED_SUMMARY_BUILDER,
  type RelationshipPresetKey,
} from './consumerContractRegistry.relationships';
import { resolveSeedKeyForField } from './consumerContractRegistry.seedDefinitions';

function slugify(fieldKey: string): string {
  return fieldKey.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
}

export interface SubOptions {
  subscriptionKind?: SubscriptionKind;
  viaBuilder?: string;
  excludeFromFieldIndex?: boolean;
  idSuffix?: string;
  operationPreset: OperationPresetKey;
  relationshipPreset?: RelationshipPresetKey;
  domainOwner?: DomainOwner;
  currentReadSource?: string;
  operationOverride?: Partial<SubscriptionOperationProfile>;
  relationshipOverride?: Partial<SubscriptionRelationshipProfile>;
}

function resolveRelationship(
  options: SubOptions,
  sourcePath: string,
  fieldDomainOwner?: DomainOwner,
): SubscriptionRelationshipProfile {
  const readSource = options.currentReadSource ?? sourcePath;
  const domainOwner = options.domainOwner ?? fieldDomainOwner ?? 'canonical_window';

  let presetKey = options.relationshipPreset;
  if (!presetKey) {
    switch (options.operationPreset) {
      case 'storedPngCachedRead':
        presetKey = 'cachedDerivativeNoPropagation';
        break;
      case 'legacyKvFallback':
        presetKey = 'legacyKvSeparatePath';
        break;
      case 'fieldOmittedSanitize':
        presetKey = 'omittedField';
        break;
      case 'canonicalPayloadRead':
        presetKey = 'canonicalPayloadReference';
        break;
      case 'compatibilityOverlayCalculate':
        presetKey = 'compatibilityOverlay';
        break;
      case 'inMemoryCalculate':
        presetKey = 'inMemoryBasketProjection';
        break;
      case 'inMemoryCalculateDriftRisk':
        presetKey = 'compatibilityOverlay';
        break;
      default:
        if (options.viaBuilder === PUBLIC_PDF_MAPPER) {
          presetKey = domainOwner === 'quote' ? 'publicPdfMapperQuoteField' : 'publicPdfMapperField';
        } else if (options.viaBuilder === SHARED_SUMMARY_BUILDER) {
          presetKey = domainOwner === 'quote' ? 'sharedBuilderQuoteField' : 'sharedBuilderAutomatic';
        } else {
          presetKey = domainOwner === 'quote' ? 'directQuoteAutomatic' : 'directAutomatic';
        }
    }
  }

  return {
    ...relationshipPreset(presetKey, readSource, domainOwner),
    ...options.relationshipOverride,
  };
}

export function sub(
  artifactId: string,
  fieldKey: string,
  subscribedField: string,
  sourceCategory: SourceCategory,
  sourcePath: string,
  outputLabelOrSlot: string,
  usage: UsageKind,
  transformationRule: string,
  fallbackBehaviour: string,
  codeReference: string,
  options: SubOptions,
): ConsumerContractSubscription {
  const subscriptionKind = options.subscriptionKind ?? 'direct';
  const kindSlug = subscriptionKind === 'direct' ? 'direct' : subscriptionKind;
  const suffix = options.idSuffix ? `-${options.idSuffix}` : '';
  const operation = { ...operationPreset(options.operationPreset), ...options.operationOverride };
  const relationship = resolveRelationship(options, sourcePath, options.domainOwner);

  return {
    id: `${artifactId}.${slugify(fieldKey)}-${kindSlug}${suffix}`,
    seedKey: resolveSeedKeyForField(fieldKey),
    fieldKey,
    subscribedField,
    sourceCategory,
    sourcePath,
    outputLabelOrSlot,
    usage,
    transformationRule,
    fallbackBehaviour,
    codeReference,
    subscriptionKind,
    viaBuilder: options.viaBuilder,
    excludeFromFieldIndex: options.excludeFromFieldIndex,
    domainOwner: relationship.domainOwner,
    currentReadSource: relationship.currentReadSource,
    accessMode: operation.accessMode,
    operation: operation.operation,
    writeBack: operation.writeBack,
    additionalPersistedCopy: operation.additionalPersistedCopy,
    riskClassification: operation.riskClassification,
    riskRationale: operation.riskRationale,
    changePropagation: relationship.changePropagation,
    propagationRationale: relationship.propagationRationale,
    safeguard: relationship.safeguard,
    relationshipKind: relationship.relationshipKind,
  };
}

export function buildSummaryBuilderAggregate(
  consumer: SummaryRowConsumerArtifact,
): ConsumerContractSubscription {
  const isPublicPdf = consumer.viaBuilder === PUBLIC_PDF_MAPPER;
  return sub(
    consumer.artifactId,
    '_summary_rows_builder',
    'Shared designer summary row builder',
    'derived_cache',
    `${consumer.metaInputPath} → ${consumer.viaBuilder}`,
    'All summary-row labels (KV table / spec list)',
    consumer.usage,
    `Invokes ${consumer.viaBuilder} to produce label/value rows consumed by this artifact`,
    'Empty rows filtered out; staff/public PDF may fall back to legacy flat KV when no rows',
    `${GET_DESIGNER_SUMMARY_ROWS_REF}; ${consumer.codeReference}`,
    {
      subscriptionKind: 'aggregate',
      viaBuilder: consumer.viaBuilder,
      excludeFromFieldIndex: true,
      idSuffix: 'builder',
      operationPreset: isPublicPdf ? 'publicPdfMapperAggregate' : 'summaryBuilderAggregate',
      relationshipPreset: isPublicPdf ? 'publicPdfMapperAggregate' : undefined,
      domainOwner: 'derived_cache',
      currentReadSource: `${consumer.metaInputPath} → ${consumer.viaBuilder}`,
    },
  );
}

export function buildIndirectSummaryFieldSubscription(
  consumer: SummaryRowConsumerArtifact,
  field: SummaryFieldDefinition,
): ConsumerContractSubscription {
  const isPublicPdf = consumer.viaBuilder === PUBLIC_PDF_MAPPER;
  return sub(
    consumer.artifactId,
    field.fieldKey,
    field.subscribedField,
    field.sourceCategory,
    field.sourcePath,
    field.outputLabel,
    consumer.usage,
    isPublicPdf
      ? `${field.transformationRule} (Public PDF mapper — parallels ${GET_DESIGNER_SUMMARY_ROWS_REF})`
      : `${field.transformationRule} via ${consumer.viaBuilder}`,
    field.fallbackBehaviour,
    `${GET_DESIGNER_SUMMARY_ROWS_REF}; ${consumer.codeReference}`,
    {
      subscriptionKind: 'indirect',
      viaBuilder: consumer.viaBuilder,
      operationPreset: isPublicPdf ? 'publicPdfMapperReadFormat' : 'summaryRowIndirectReadFormat',
      domainOwner: field.domainOwner,
      currentReadSource: field.sourcePath,
      relationshipPreset: isPublicPdf
        ? field.domainOwner === 'quote'
          ? 'publicPdfMapperQuoteField'
          : 'publicPdfMapperField'
        : field.domainOwner === 'quote'
          ? 'sharedBuilderQuoteField'
          : 'sharedBuilderAutomatic',
    },
  );
}

export function buildAllSummaryRowSubscriptions(): ConsumerContractSubscription[] {
  const subs: ConsumerContractSubscription[] = [];
  for (const consumer of SUMMARY_ROW_CONSUMER_ARTIFACTS) {
    subs.push(buildSummaryBuilderAggregate(consumer));
    for (const field of DESIGNER_SUMMARY_FIELD_DEFINITIONS) {
      if (consumer.fieldKeys && !consumer.fieldKeys.includes(field.fieldKey)) continue;
      subs.push(buildIndirectSummaryFieldSubscription(consumer, field));
    }
  }
  return subs;
}

export function summarySubscriptionsForArtifact(artifactId: string): ConsumerContractSubscription[] {
  return buildAllSummaryRowSubscriptions().filter((s) => s.id.startsWith(`${artifactId}.`));
}

export function countUniqueSummaryFields(): number {
  return DESIGNER_SUMMARY_FIELD_DEFINITIONS.length;
}

export function countSummaryRowConsumerArtifacts(): number {
  return SUMMARY_ROW_CONSUMER_ARTIFACTS.length;
}
